import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Form } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import {
  DRAWER_HEADER_ACTIONS_ID,
  DRAWER_HEADER_ACTIONS_ID_MOBILE,
  ROUTES,
  USER_LIBRARY_TYPE,
} from '../../../../../common/constants';
import GoBackButton from '../../../../../components/GoBackButton';
import LoaderComponent from '../../../../../components/LoaderComponent';
import Portal from '../../../../../components/Portal';
import usePiiReveal, {
  clearRevealedPiiForUser,
} from '../../../../../hooks/usePiiReveal';
import { UPDATE_USER } from '../../../graphql/Mutations';
import { USER } from '../../../graphql/Queries';
import EndUserForm from '../components/EndUserForm';
import EndUsersTabs from '../components/EndUsersTabs';

const EditEndUser = ({
  match: { params: { id, userNumber } = {} } = {},
  isDrawerView = false,
  // Props for drawer mode
  endUserId: drawerUserId,
  endUserNumber: drawerUserNumber,
}) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const {
    dispatch,
    state: { isDesktop },
  } = useContext(AppContext);
  const [endUserDetail, setEndUserDetail] = useState({});
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isEndUserLoading, setIsEndUserLoading] = useState(true);
  const currentId = isDrawerView ? drawerUserId : id;
  const currentUserNumber = isDrawerView ? drawerUserNumber : userNumber;
  const { revealedPii, isPiiMaskingBypassed } = usePiiReveal({
    userId: currentId,
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onError: () => { },
  });

  const [users] = useLazyQuery(USER, {
    onCompleted: (response) => {
      dispatch({
        type: 'SET_END_USERS_ONBOARDING_DATE',
        data: response?.user?.createdAt,
      });
      setEndUserDetail(response?.user);
      setIsEndUserLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() { },
  });

  useEffect(() => {
    if (currentId) {
      setIsEndUserLoading(true);
      setEndUserDetail([]);
      users({
        variables: {
          where: {
            id: currentId,
          },
        },
      });
    }
  }, [currentId]);

  const updateAdminFun = async (values) => {
    setIsSubmitLoading(true);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
    const isPiiVisible = !!revealedPii || isPiiMaskingBypassed;
    const isEmailEditable = isPiiVisible || !endUserDetail?.email;
    const isContactEditable = isPiiVisible || !endUserDetail?.contactNo;
    const isEmailEdited = isEmailEditable && form?.isFieldTouched('email');
    const isContactEdited = isContactEditable && form?.isFieldTouched('contact');

    const response = await updateUser({
      variables: {
        where: {
          id: currentId,
        },
        data: {
          firstName: values?.firstName?.trim(),
          lastName: values?.lastName?.trim(),
          gender: values?.gender,
          city: values?.city?.trim(),
          state: values?.state?.trim(),
          address: values?.address?.trim(),
          address2: values?.address2?.trim(),
          locality: values?.locality?.trim(),
          pincode: values?.pincode,
          ...(isContactEdited ? { contactNo: values?.contact?.trim() } : {}),
          ...(isEmailEdited ? { email: values?.email?.trim() } : {}),
        },
      },
    });
    if (response?.data) {
      if (isEmailEdited || isContactEdited) {
        clearRevealedPiiForUser(currentId);
        form?.setFields([
          { name: 'email', touched: false },
          { name: 'contact', touched: false },
        ]);
      }
      if (isDrawerView) {
        dispatch({
          type: 'SET_REFETCH_END_USERS',
          data: true,
        });
      } else {
        history?.replace(`${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}`);
      }
    }
    setIsSubmitLoading(false);
  };

  const handleShowPrompt = () => {
    setIsDisabled(false);
    if (!isDrawerView) {
      dispatch({
        type: 'SET_SHOW_PROMPT',
        data: true,
      });
    }
  };

  const primaryLibrary = endUserDetail?.userLibraries?.find(
    (lib) => lib?.type === USER_LIBRARY_TYPE?.PRIMARY?.value,
  );

  const initialValues = {
    ...endUserDetail,
    contact: endUserDetail?.contactNo,
    status: endUserDetail?.isActive,
    state: endUserDetail?.state,
    city: endUserDetail?.city,
    pincode: endUserDetail?.pincode?.pincode,
    id: endUserDetail?.userNumber || currentUserNumber,
    mappedLibrary:
      primaryLibrary?.library?.name || endUserDetail?.pincode?.library?.name,
    lat: endUserDetail?.latLong?.coordinates?.[1],
    lng: endUserDetail?.latLong?.coordinates?.[0],
    additionalAddress:
      endUserDetail?.additionalAddressDetails?.formattedAddress,
  };

  const ActionButtons = () => (
    <div className="header-btn-wrapper">
      {!isDrawerView && (
        <Button
          className="common-button mr-16"
          size="middle"
          onClick={() => {
            history?.push(`${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}`);
          }}
        >
          Discard Changes
        </Button>
      )}
      <Button
        className="common-button"
        size="middle"
        onClick={form.submit}
        loading={isSubmitLoading}
        disabled={isDisabled}
      >
        Save
      </Button>
    </div>
  );

  const contentComponent = isEndUserLoading ? (
    <LoaderComponent size="large" setHeight="60" spinning={isEndUserLoading} />
  ) : (
    <div className="endUser-form">
      <EndUserForm
        form={form}
        onFinish={updateAdminFun}
        handleShowPrompt={handleShowPrompt}
        initialValues={initialValues}
        endUserDetail={endUserDetail}
        userId={currentId}
      />
    </div>
  );

  if (isDrawerView) {
    return (
      <>
        {isDesktop ? (
          <Portal portalId={DRAWER_HEADER_ACTIONS_ID}>
            <div className="drawer-header-actions">
              <ActionButtons />
            </div>
          </Portal>
        ) : (
          <Portal portalId={DRAWER_HEADER_ACTIONS_ID_MOBILE}>
            <div className="drawer-header-actions">
              <ActionButtons />
            </div>
          </Portal>
        )}
        {contentComponent}
      </>
    );
  }

  return (
    <>
      <Portal portalId="header-title-content">
        <GoBackButton
          customLink={`${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}`}
        />
        <div className="portal-header">Edit End User</div>
      </Portal>
      <Portal portalId="header-right-content">
        <ActionButtons />
      </Portal>
      <Portal portalId="header-right-content-phones">
        <ActionButtons />
      </Portal>
      <EndUsersTabs
        endUserId={currentId}
        endUserNumber={currentUserNumber}
        onboardedOn={endUserDetail?.createdAt}
      >
        {contentComponent}
      </EndUsersTabs>
    </>
  );
};

export default EditEndUser;
